import authRepository from '~/repositories/v1/authRepository'
import userRepository from '~/repositories/v1/userRepository'
import utilsRepository from '~/repositories/v1/utilsRepository'
import prizesRepository from '~/repositories/lotteries/prizesRepository'
import classicPrizesRepository from '~/repositories/lotteries/classicPrizesRepository'
import subscriberRepository from '~/repositories/v1/subscriberRepository'
import notificationRepository from '~/repositories/lotteries/notificationRepository'
import lotteryRepository from '~/repositories/v1/lotteryRepository'
import userLotteryRepository from '~/repositories/v1/userLotteryRepository'
import userNotificationsRepository from '~/repositories/v1/userNotificationsRepository'

/**
 * Factory that assembles the full repository map injected as `$repository` throughout the app.
 * Separates the v1 (main API) and lotteries namespaces.
 */
export default (_$axios: any, _$fetchLotteries: any) => {
	return {
		v1: {
			auth: authRepository,
			user: userRepository,
			utils: utilsRepository,
			subscriber: subscriberRepository,
			lottery: lotteryRepository,
			userLottery: userLotteryRepository,
			notifications: userNotificationsRepository,
		},
		lotteries: {
			prizes: prizesRepository,
			classicPrizes: classicPrizesRepository,
			notification: notificationRepository,
		},
	}
}

// Helper para API base ($axios)
/**
 * Generic HTTP helper for the main (`$axios`) API client.
 * Normalises axios responses so callers always receive `response.data` directly.
 * Returns a `{ success: false }` POJO on error instead of throwing.
 * @param method - HTTP verb (`'get'`, `'post'`, `'put'`, `'delete'`).
 * @param url - Path relative to the base URL.
 * @param params - Request body or query params forwarded to axios.
 */
export async function useRequest<T = any>(method: string, url: string, params?: any): Promise<T> {
	const { $axios } = useNuxtApp()
	const m = method.toLowerCase()
	const fn = ($axios as any)?.[m]

	if (typeof fn !== 'function') {
		return { success: false, message: `axios missing method: ${m}` } as any
	}

	try {
		const res = await fn(url, params)
		// axios => res.data
		return (res?.data ?? res) as T
	} catch (err: any) {
		// tu interceptor antiguo devolvía err.data
		return err?.data ?? err?.response?.data ?? ({ success: false, message: 'Request error' } as any)
	}
}

// Helper para API lotteries ($fetchLotteries) — puede ser axios o $fetch
/**
 * Generic HTTP helper for the lotteries (`$fetchLotteries`) API client.
 * Works with either an axios instance or a `$fetch`-based wrapper.
 * Returns a `{ success: false }` POJO (never throws) to keep SSR/payload serialisation safe.
 * @param method - HTTP verb (`'get'`, `'post'`, etc.).
 * @param url - Path relative to the lotteries API base URL.
 * @param params - Request body or query params.
 */
export async function useRequestLotteries<T = any>(method: string, url: string, params?: any): Promise<T> {
	const nuxtApp = useNuxtApp()
	const client: any = (nuxtApp as any).$fetchLotteries
	const m = method.toLowerCase()
	const fn = client?.[m]

	// ✅ si no existe, devuelve POJO (no revienta SSR/payload)
	if (typeof fn !== 'function') {
		return {
			success: false,
			message: `fetchLotteries missing method: ${m}`,
			meta: { hasClient: !!client, isServer: import.meta.server, isClient: import.meta.client },
		} as any
	}

	try {
		const res = await fn(url, params)
		// axios => res.data, $fetch => res
		return (res?.data ?? res) as T
	} catch (err: any) {
		return err?.data ?? err?.response?.data ?? ({ success: false, message: 'Request error' } as any)
	}
}

// export function useRequest<T = any>(method: string, url: string, params?: any): Promise<T> | null {
// 	return useFetch[method](url, params)
// 		.then((res: any) => res.data as T)
// 		.catch((err: any) => err.data)
// }

// export function useRequestLotteries<T = any>(method: string, url: string, params?: any): Promise<T> | null {
// 	return useFetchLotteries[method](url, params)
// 		.then((res: any) => res.data as T)
// 		.catch((err: any) => err.data)
// }
